/* Product page: the gallery (thumbnails, swipe, arrow keys), the chosen pack's legal
   line, food-information pack line and button price, the button state and ?variant=
   in the URL. The packs are a native radio group (name="id") inside the product form,
   so the page works without JS; add to cart is handled by taman.js
   (form[data-product-form]). Idempotent init; re-runs on shopify:section:load. */
(() => {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  class Gallery {
    constructor(root) {
      this.viewport = root.querySelector('[data-gallery-viewport]');
      this.list = root.querySelector('[data-gallery-thumbs]');
      this.slides = [...root.querySelectorAll('[data-gallery-slide]')];
      this.thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
      this.index = 0;
      this.target = null;
      if (!this.viewport || this.slides.length < 2) return;

      this.thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => this.go(i));
        thumb.addEventListener('keydown', (e) => this.onKey(e, i));
      });

      /* follow swipes and keyboard scrolling: the slide that is mostly in view wins */
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) this.settle(this.slides.indexOf(entry.target));
          });
        }, { root: this.viewport, threshold: [0.6] });
        this.slides.forEach((slide) => this.observer.observe(slide));
      } else {
        let frame = 0;
        this.viewport.addEventListener('scroll', () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => this.sync());
        }, { passive: true });
      }
      this.viewport.addEventListener('scrollend', () => this.sync());
      let resizing = 0;
      window.addEventListener('resize', () => {
        clearTimeout(resizing);
        resizing = setTimeout(() => this.sync(), 150);
      }, { passive: true });
    }

    go(i, instant = false) {
      const slide = this.slides[i];
      if (!slide) return;
      this.target = i;
      clearTimeout(this.release);
      this.release = setTimeout(() => { this.target = null; this.sync(); }, 900);
      this.viewport.scrollTo({ left: slide.offsetLeft, behavior: instant || REDUCED.matches ? 'auto' : 'smooth' });
      this.mark(i);
    }

    goToMedia(id, instant = false) {
      if (!this.viewport) return;
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(id));
      if (i > -1 && i !== this.index) this.go(i, instant);
    }

    /* a programmatic scroll owns the thumbnails until it lands on its target */
    settle(i) {
      if (i < 0) return;
      if (this.target !== null) {
        if (i === this.target) this.target = null;
        return;
      }
      if (i !== this.index) this.mark(i);
    }

    sync() {
      const width = this.viewport.clientWidth || 1;
      this.settle(Math.max(0, Math.min(this.slides.length - 1, Math.round(this.viewport.scrollLeft / width))));
    }

    mark(i) {
      this.index = i;
      this.thumbs.forEach((t, n) => t.setAttribute('aria-current', n === i ? 'true' : 'false'));
      const active = this.thumbs[i];
      if (this.list && active && this.list.scrollWidth > this.list.clientWidth) {
        const item = active.parentElement;
        const left = item.offsetLeft - (this.list.clientWidth - item.offsetWidth) / 2;
        this.list.scrollTo({ left, behavior: REDUCED.matches ? 'auto' : 'smooth' });
      }
    }

    onKey(e, i) {
      const last = this.thumbs.length - 1;
      let next = null;
      if (e.key === 'ArrowRight') next = i === last ? 0 : i + 1;
      else if (e.key === 'ArrowLeft') next = i === 0 ? last : i - 1;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = last;
      if (next === null) return;
      e.preventDefault();
      this.thumbs[next].focus();
      this.go(next);
    }
  }

  class Packs {
    constructor(section, gallery) {
      this.section = section;
      this.gallery = gallery;
      this.group = section.querySelector('[data-packs]');
      this.cta = section.querySelector('[data-pdp-cta]');
      this.ctaLabel = section.querySelector('[data-pdp-cta-label]');
      this.error = section.querySelector('[data-form-error]');
      if (!this.group) return;

      this.group.addEventListener('change', (e) => {
        const input = e.target;
        if (!(input instanceof HTMLInputElement) || input.name !== 'id') return;
        this.apply(input);
        this.writeUrl(input.value);
        if (this.gallery && input.dataset.mediaId) this.gallery.goToMedia(input.dataset.mediaId);
      });

      /* the browser may restore a different radio than the server rendered:
         its lines, price and photo follow without an animated scroll */
      const checked = this.group.querySelector('input[name="id"]:checked');
      if (checked) {
        this.apply(checked);
        if (!checked.defaultChecked && this.gallery && checked.dataset.mediaId) this.gallery.goToMedia(checked.dataset.mediaId, true);
      }
    }

    apply(input) {
      const id = input.value;
      this.section.querySelectorAll('[data-variant-show]').forEach((el) => {
        el.hidden = el.dataset.variantShow !== id;
      });
      if (this.cta) {
        const available = input.dataset.available === 'true';
        this.cta.disabled = !available;
        if (this.ctaLabel) this.ctaLabel.textContent = available ? this.cta.dataset.labelAdd : this.cta.dataset.labelSoldout;
      }
      if (this.error) this.error.hidden = true;
    }

    writeUrl(id) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', id);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (err) {
        /* history is not writable here (sandboxed preview) */
      }
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-pdp]').forEach((section) => {
      if (section.dataset.pdpReady) return;
      section.dataset.pdpReady = '1';
      const galleryRoot = section.querySelector('[data-gallery]');
      const gallery = galleryRoot ? new Gallery(galleryRoot) : null;
      section.pdpPacks = new Packs(section, gallery);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
